import React, { useEffect, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { FirmwareItem } from '../../types/otaUpdate';
import { FirmwareDetailModal } from './FirmwareDetailModal';
import { useOtaOverview } from './otaOverview/useOtaOverview';
import { OtaStatsGrid } from './otaOverview/OtaStatsGrid';
import { DeviceStatusTab } from './otaOverview/DeviceStatusTab';
import { FirmwareTab } from './otaOverview/FirmwareTab';
import { PendingUpdatesTab } from './otaOverview/PendingUpdatesTab';

type Tab = 'overview' | 'firmware' | 'pending';

/**
 * The OTA screen: the figures, three tabs (gateway status, firmware
 * catalogue, scheduled updates) and the firmware dialog. The data lives in
 * `useOtaOverview`.
 */
export const OtaUpdatesOverview: React.FC<{ refreshTrigger?: number }> = ({ refreshTrigger }) => {
  const { t } = useI18n();
  const ota = useOtaOverview(refreshTrigger);
  const [activeTab, setActiveTab] = useState<Tab>('firmware');
  const [selectedFirmware, setSelectedFirmware] = useState<FirmwareItem | null>(null);

  // After a save, the open dialog shows the row as the API now returns it.
  useEffect(() => {
    if (!selectedFirmware) return;
    const updated = ota.firmwareItems.find((fw) => fw.id === selectedFirmware.id);
    if (updated && updated !== selectedFirmware) setSelectedFirmware(updated);
  }, [ota.firmwareItems, selectedFirmware]);

  if (ota.loading) {
    return (
      <div className="ota-loading">
        <div className="loading-spinner" />
        <p>{t('otaUpdates.loading')}</p>
      </div>
    );
  }

  if (ota.error) {
    return (
      <div className="ota-error">
        <span className="error-icon">!</span>
        <h3>{t('otaUpdates.setupRequired')}</h3>
        <p>{ota.error}</p>
      </div>
    );
  }

  const tabButton = (tab: Tab, label: string, badge?: number) => (
    <button type="button" className={`tab-button ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
      {label}
      {badge ? <span className="tab-badge">{badge}</span> : null}
    </button>
  );

  const { figures } = ota;

  return (
    <div className="ota-updates-container">
      <OtaStatsGrid figures={figures} />

      <div className="ota-section">
        <div className="section-tabs">
          {tabButton('overview', t('otaUpdates.tabDeviceStatus'))}
          {tabButton('firmware', t('otaUpdates.firmwareVersions'))}
          {tabButton('pending', t('otaUpdates.tabPending'), ota.pendingUpdates.length)}
        </div>

        <div className="section-content">
          {activeTab === 'overview' && <DeviceStatusTab ota={ota} />}
          {activeTab === 'firmware' && <FirmwareTab ota={ota} onOpen={setSelectedFirmware} />}
          {activeTab === 'pending' && <PendingUpdatesTab updates={ota.pendingUpdates} />}
        </div>
      </div>

      <div className="ota-footer">
        <div className="activity-summary">
          <div className="activity-item">
            <span className="activity-icon">📦</span>
            <span className="activity-text"><strong>{figures.firmwareCount}</strong> {t('otaUpdates.footerFirmware')}</span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">📱</span>
            <span className="activity-text"><strong>{figures.totalDevices}</strong> {t('otaUpdates.footerDevices')}</span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">🔧</span>
            <span className="activity-text"><strong>{figures.modelCount}</strong> {t('otaUpdates.footerModels')}</span>
          </div>
        </div>
      </div>

      <FirmwareDetailModal
        isOpen={selectedFirmware !== null}
        firmware={selectedFirmware}
        onClose={() => setSelectedFirmware(null)}
        onSave={ota.reload}
        onDelete={ota.reload}
      />
    </div>
  );
};
